/**
 * Animal Attack — entry point.
 *
 * Designed by Kate, age 9. See REQUIREMENTS.md; the design is hers and is not to be
 * "improved". This file wires the M0 foundation together: renderer, fixed-timestep
 * loop, world, player, and the third-person camera that follows them.
 */
import './style.css'
import * as THREE from 'three'
import { GameLoop } from './engine/loop'
import { bindResize, createRenderer } from './engine/renderer'
import { Keyboard } from './input/keyboard'
import { LookControl } from './input/look'
import { SettingsStore, keyLabel } from './input/settings'
import { TouchControls } from './input/touch'
import { Player } from './player/player'
import { BurrowField, BURROW_RADIUS } from './animals/burrow'
import { SPECIES_ORDER, SPECIES } from './animals/species'
import { AnimalAgent } from './animals/agent'
import { buildAnimal, animate } from './animals/models'
import { REAL_ANIMALS, realClearanceRatio,
         PROPORTION_TOLERANCE } from './animals/proportions'
import { Bite, BITE_RANGE } from './animals/combat'
import { killsInstantly, SniffTrail } from './animals/abilities'
import { canEat, IDENTITY } from './animals/identity'
import { TRAMPLE_RADIUS, VERBS } from './animals/verbs'
import { ThirdPersonCamera } from './player/thirdPersonCamera'
import { BIOME_ORDER, BIOMES } from './world/biomes'
import { DayNightCycle } from './world/dayNight'
import { Weather } from './world/weather'
import { createWorld } from './world/world'
import { SkyEnvironment } from './world/env'
import { heightAt, LANDMARKS } from './world/terrain'
import { BerryPatch, BERRY_FEEDS, FishShoal, FISH_FEEDS } from './world/forage'
import { SettingsScreen } from './ui/settingsScreen'
import { ShopScreen } from './ui/shopScreen'
import { Economy } from './economy/economy'
import { CouponField } from './economy/pickups'
import { Hud } from './ui/hud'
import { Minimap, PLAYER_COLORS, type MapDot } from './ui/minimap'
import { Chat } from './ui/chat'
import { CardCollection, CARD_KINDS, kindOf } from './cards/cards'
import { CardScreen } from './ui/cardScreen'
import { CustomizeScreen } from './ui/customizeScreen'
import { RoomClient, roomPopulation } from './net/room'
import { createNameTag, type NameTag } from './ui/nameTag'
import { StartScreen } from './ui/startScreen'
import { TouchMenu } from './ui/touchMenu'
import { Audio } from './audio/audio'
import { Feedback } from './ui/feedback'
import { MapScreen } from './ui/mapScreen'
import { Inventory, meatFrom } from './economy/inventory'
import { InventoryBar } from './ui/inventoryBar'
import { Competition, COMPETITION_ORDER, COMPETITIONS, CHECKPOINT_RADIUS } from './competitions/competitions'
import { CompetitionScheduler } from './competitions/scheduler'
import { Discovered } from './animals/discovered'

/** Keep a combined keyboard + touch axis within [-1, 1]. */
function clampAxis(value: number): number {
  return Math.max(-1, Math.min(1, value))
}

/** Darkest the world is allowed to get, so a night storm stays playable. */
/** The eye height of the human, so other species can be framed relative to it. */
const HUMAN_EYE_HEIGHT = 1.46
const MIN_SUN_INTENSITY = 0.28
/** Water is a bright tropical blue by day and near-black at night, lit only by the moon. */
const DAY_WATER = new THREE.Color(0x2d6d8c)
const NIGHT_WATER = new THREE.Color(0x0c1c2a)
const MIN_SKY_INTENSITY = 1.8

/** Set once the customize screen exists; chat uses whatever name the player typed. */
let customizeName: () => string = () => 'You'

function boot(): void {
  const canvas = document.querySelector<HTMLCanvasElement>('#game')
  if (!canvas) throw new Error('missing #game canvas')

  const renderer = createRenderer(canvas)
  const world = createWorld()
  const { scene } = world
  const skyEnv = new SkyEnvironment(renderer, world.sky3d.mesh)
  const player = new Player()
  scene.add(player.object)

  const burrows = new BurrowField()
  const burrowMarkers = new THREE.Group()
  scene.add(burrowMarkers)

  const aspect = (canvas.clientWidth || window.innerWidth) / (canvas.clientHeight || window.innerHeight)
  const rig = new ThirdPersonCamera(aspect)
  bindResize(renderer, rig.camera, canvas)

  const cycle = new DayNightCycle()
  const weather = new Weather()
  scene.add(weather.rain)
  scene.add(weather.funnel)

  const settings = new SettingsStore()

  // AI animals. Kate: in single player "all of them are AI. They're just AI people
  // playing the game" — stand-ins for players, not hostile mobs.
  const animals: AnimalAgent[] = []
  const spawnAnimals = (): void => {
    for (const animal of animals) scene.remove(animal.object)
    animals.length = 0
    const kinds = ['deer', 'wolf', 'bunny', 'bird', 'cat', 'bear'] as const
    for (let i = 0; i < 12; i++) {
      const angle = (i / 12) * Math.PI * 2
      const radius = 14 + (i % 4) * 7
      const agent = new AnimalAgent(
        `ai-${i}`, kinds[i % kinds.length]!,
        Math.cos(angle) * radius, Math.sin(angle) * radius,
        settings.current.difficulty, 1000 + i,
      )
      animals.push(agent)
      scene.add(agent.object)
    }
  }
  spawnAnimals()

  const economy = new Economy()
  let coupons = new CouponField(180, 9)
  scene.add(coupons.mesh)

  /**
   * Show only the controls this animal actually has.
   *
   * Kate, 2026-08-24, playing as a human: "what does the blue button do? It doesn't do
   * anything about attack. And below, burrow. What does that do? It doesn't do
   * anything." She was right --- a human has no verb, so the blue button sat there
   * doing nothing, and this only ever ran after a morph, so at the start of a game it
   * had never run at all.
   */
  const refreshSpeciesControls = (): void => {
    const verb = player.verb
    touch.setVerb(verb ? VERBS[verb].label : null)
    touch.setDigLabel(player.species.id === 'bird' ? 'nest'
      : player.species.id === 'human' ? 'none' : 'burrow')
  }

  const shopScreen = new ShopScreen(economy)
  shopScreen.onMorph = (id) => {
    player.morph(id)
    room.setSpecies(id)
    audio.play('morph')
    feedback.toast(`You are now a ${SPECIES[id].name.toLowerCase()}`, 'good')
    // Tell the player what being this animal actually means, rather than leaving them
    // to infer it from a stat line.
    window.setTimeout(() => feedback.toast(IDENTITY[id].feel, 'info'), 900)
    if (discovered.record(id)) {
      audio.play('card')
      window.setTimeout(() => feedback.toast(
        `New animal! ${discovered.count} of ${discovered.total} discovered`, 'good'), 1600)
    }
    refreshSpeciesControls()
    const verb = player.verb
    if (verb) window.setTimeout(() => feedback.toast(VERBS[verb].hint, 'info'), 2200)
  }
  shopScreen.onPurchase = (label) => { audio.play('purchase'); feedback.toast(`Bought ${label}`, 'good') }
  shopScreen.onRefused = (why) => { audio.play('lose'); feedback.toast(why, 'bad') }
  window.addEventListener('keydown', (e) => {
    if (e.code !== 'KeyP' || chat.isTyping) return
    releasePointer()
    shopScreen.toggle({ speciesId: player.species.id, biome: world.biome })
  })

  // Competitions — the only thing you win. Kate reversed herself on this: "there's no
  // winning the games... competitions are little mini games that you win."
  let competition: Competition | null = null
  const checkpointMarkers = new THREE.Group()
  scene.add(checkpointMarkers)

  const startCompetition = (kind: (typeof COMPETITION_ORDER)[number]): void => {
    competition = new Competition(kind)
    checkpointMarkers.clear()
    for (const point of competition.checkpoints) {
      const marker = new THREE.Mesh(
        new THREE.TorusGeometry(CHECKPOINT_RADIUS * 0.8, 0.22, 8, 20),
        new THREE.MeshStandardMaterial({ color: 0x6de3ff, emissive: 0x1d6b85, emissiveIntensity: 0.7 }),
      )
      marker.rotation.x = -Math.PI / 2
      marker.position.set(point.x, 1.2, point.z)
      checkpointMarkers.add(marker)
    }
    chat.add({ from: 'Game', text: `${COMPETITIONS[kind].name} started — ${COMPETITIONS[kind].description}`, color: '#6de3ff' })
  }

  window.addEventListener('keydown', (e) => {
    if (e.code !== 'KeyK' || chat.isTyping) return
    const index = competition ? COMPETITION_ORDER.indexOf(competition.kind) + 1 : 0
    startCompetition(COMPETITION_ORDER[index % COMPETITION_ORDER.length]!)
  })

  // Squishy cards. Kate: "it's only when you win a competition."
  const cards = new CardCollection()
  const cardScreen = new CardScreen(cards)
  cardScreen.onAccept = (couponsPaid) => economy.collect(couponsPaid)
  window.addEventListener('keydown', (e) => {
    if (e.code === 'KeyC' && !chat.isTyping) { releasePointer(); cardScreen.toggle() }
  })

  // The bar on the side. Kate: "there's not name tags, it's just a little bar on the
  // side. You can push it and customize character."
  // Every action needs a route that does not involve a keyboard — this is mainly an
  // iPad and phone game, and all of these were key-only.
  // Kate: an inventory bar you can look at, and meat from every kill.
  const inventory = new Inventory()

  const mapScreen = new MapScreen()
  /*
   * Kate, 2026-08-23: "I can't move from jungle to ocean or jungle to desert."
   *
   * A vote is only meaningful when there is somebody to vote with. Since the game
   * started auto-joining the shared room, every session counted as online, so a lone
   * player picking a map got "asked everyone to agree" and a silent server round trip
   * instead of a new map --- which reads exactly like the button not working.
   */
  mapScreen.isOnline = () => room.players.size > 0
  mapScreen.currentBiome = () => world.biome
  mapScreen.onChoose = (biome, online) => {
    if (online) {
      room.voteBiome(biome)
      feedback.toast('Asked everyone to agree to the new map', 'info')
    } else {
      changeBiome(biome)
      feedback.toast(`Map changed to ${BIOMES[biome].name}`, 'good')
    }
  }

  /** Kate: "whenever I kill an animal, I still get meat from it." */
  const dropMeat = (speciesId: keyof typeof SPECIES): void => {
    const meat = meatFrom(SPECIES[speciesId].name)
    inventory.add(meat)
    inventoryBar.render()
    feedback.toast(`You caught the ${SPECIES[speciesId].name.toLowerCase()} · got ${meat.label}`, 'good')
  }

  const releasePointer = (): void => {
    if (document.pointerLockElement) document.exitPointerLock()
  }

  const touchMenu = new TouchMenu([
    { id: 'shop', label: 'Shop — buy a morph tool', icon: '🛒',
      run: () => { releasePointer(); closeScreensExcept('shop')
                   shopScreen.toggle({ speciesId: player.species.id, biome: world.biome }) } },
    { id: 'cards', label: 'Squishy cards', icon: '🃏',
      run: () => { releasePointer(); closeScreensExcept('cards'); cardScreen.toggle() } },
    // Kate: "you still haven't made it so you can change maps." It was keyboard-only and
    // offline-gated, so on the iPad she plays on there was no way to reach it at all.
    { id: 'map', label: 'Change the map', icon: '🗺️',
      run: () => { releasePointer(); closeScreensExcept('map'); mapScreen.open() } },
    { id: 'competition', label: 'Start a competition', icon: '🏁', run: () => {
        const index = competition ? COMPETITION_ORDER.indexOf(competition.kind) + 1 : 0
        startCompetition(COMPETITION_ORDER[index % COMPETITION_ORDER.length]!)
      } },
    { id: 'dig', label: 'Dig a burrow', icon: '🕳️', run: () => { digHeldUntil = performance.now() + 2600 } },
    { id: 'chat', label: 'Chat', icon: '💬', run: () => chat.focus() },
    { id: 'name', label: 'Customize character', icon: '🙂',
      run: () => { closeScreensExcept('name'); customize.open() } },
    { id: 'settings', label: 'Settings', icon: '⚙️',
      run: () => { closeScreensExcept('settings'); settingsScreen.open() } },
    { id: 'help', label: 'How to play', icon: '❓',
      run: () => { releasePointer(); closeScreensExcept('help'); startScreen.open() } },
    { id: 'music', label: 'Music on / off', icon: '🎵',
      run: () => {
        const on = !(audio.ambience?.musicOn ?? true)
        audio.ambience?.setMusic(on)
        feedback.toast(on ? 'Music on' : 'Music off', 'info')
      } },
    { id: 'nature', label: 'Jungle sounds on / off', icon: '🦜',
      run: () => {
        const on = !(audio.ambience?.natureOn ?? true)
        audio.ambience?.setNature(on)
        feedback.toast(on ? 'Jungle sounds on' : 'Jungle sounds off', 'info')
      } },
    { id: 'debug', label: 'Show game info', icon: '📊',
      run: () => { hudRoot.dataset.debug = hudRoot.dataset.debug === 'yes' ? 'no' : 'yes' } },
    { id: 'join', label: 'Play online', icon: '🌍',
      run: () => {
        if (room.connected) { room.disconnect(); return }
        // Say who is already there before joining, rather than dropping the player into
        // what looks like a broken empty world.
        feedback.toast('Checking the room…', 'info')
        void roomPopulation('kate').then((info) => {
          if (!info) {
            feedback.toast('Cannot reach the game server — playing offline', 'bad')
            return
          }
          feedback.toast(info.players === 0
            ? 'Nobody else is here yet — joining anyway, tell someone to press Play online'
            : `${info.players} of ${info.max} already playing — joining`, 'info')
          room.connect('kate')
        })
      } },
    { id: 'vote', label: 'Agree to change the map', icon: '🗳️', run: () => room.acceptVote(),
      visible: () => room.connected },
    { id: 'family', label: 'Be a family', icon: '👪', visible: () => room.connected, run: () => {
        let nearest: string | null = null
        let best = 12
        for (const [id, other] of room.players) {
          const d = Math.hypot(other.x - player.position.x, other.z - player.position.z)
          if (d < best) { best = d; nearest = id }
        }
        if (nearest) room.requestFamily(nearest)
      } },
  ])

  /**
   * Only one full-screen sheet at a time.
   *
   * Each screen opened independently, so picking a second one from the menu stacked it
   * on the first --- the map screen sitting on top of a still-open cards screen, with
   * two Back buttons and no way to tell which one you were closing.
   */
  const closeScreensExcept = (keep: string): void => {
    if (keep !== 'shop') shopScreen.close()
    if (keep !== 'cards') cardScreen.close()
    if (keep !== 'map') mapScreen.close()
    if (keep !== 'settings') settingsScreen.close()
    if (keep !== 'name') customize.close()
    if (keep !== 'help') startScreen.close()
  }

  // Digging from the menu holds the dig for long enough to finish a burrow.
  let digHeldUntil = 0
  let biteSoundTimer = 0
  let digSoundTimer = 0
  let attackHintTimer = 0
  let sleepHintTimer = 0
  let trampleCooldown = 0
  let cannotFightTimer = 0
  let soarBlend = 0
  let grazeProgress = 0
  let nightNow = false
  let forageCooldown = 0
  let wasGrounded = true
  let stepTimer = 0
  let lastHearts = 24
  let lastWeather = ''

  const startScreen = new StartScreen()
  startScreen.onStart = () => {
    refreshTouch()
    refreshSpeciesControls()
    // Howard: "the game is multiplayer. everyone gets dropped in the same room."
    // There has only ever been one room, but you had to know to press J to reach it,
    // so two people could play at the same time and never meet. Join on start instead,
    // and fall back to the offline world in silence if the server cannot be reached.
    if (!room.connected) {
      void roomPopulation('kate').then((info) => {
        if (!info || room.connected) return
        room.connect('kate')
        feedback.toast(info.players === 0
          ? 'You are the first one here — anyone who opens the game joins you'
          : `${info.players} others already playing`, 'info')
      })
    }
  }
  startScreen.onDifficulty = (level) => {
    settings.set('difficulty', level)
    spawnAnimals()      // respawn them at the new toughness
  }
  startScreen.difficulty = settings.current.difficulty
  if (!StartScreen.seenBefore()) startScreen.open()

  const customize = new CustomizeScreen()
  customizeName = () => customize.name
  customize.onRename = (name) => { room.setName(name); return undefined }
  const announceRename = (name: string) => chat.add({ from: 'Game', text: `You are now ${name}`, color: '#6de3ff' })
  void announceRename

  // Other players, drawn with the same bodies as everyone else.
  const remoteBodies = new Map<string, THREE.Group>()
  const remoteTags = new Map<string, NameTag>()
  const room = new RoomClient({
    onStatus: (status) => chat.add({ from: 'Game', text: `Room ${status}`, color: '#6de3ff' }),
    onWelcome: (you, players) => {
      minimap.setOwnColor(you.color)
      if (customize.name !== 'You') room.setName(customize.name)
      room.setSpecies(player.species.id)
      for (const other of players) addRemote(other)
    },
    onJoined: (other) => {
      addRemote(other)
      chat.add({ from: 'Game', text: `${other.name} joined`, color: other.color })
    },
    onLeft: (id) => {
      const body = remoteBodies.get(id)
      if (body) { scene.remove(body); remoteBodies.delete(id) }
      remoteTags.get(id)?.dispose()
      remoteTags.delete(id)
    },
    onChat: (from, text, color) => chat.add({ from, text, color }),
    onBiome: (biome) => changeBiome(biome as Parameters<typeof world.setBiome>[0]),
    onVoteStarted: (biome, by, needed) => chat.add({ from: 'Vote',
      text: `${by} wants to change the map to ${biome}. Press V to agree (${needed} needed)`,
      color: '#6de3ff' }),
    onVoteProgress: (accepted, needed) => chat.add({ from: 'Vote',
      text: `${accepted} of ${needed} agree`, color: '#6de3ff' }),
    onFamily: (withName, ok, reason) => chat.add({ from: 'Family',
      text: ok ? `You and ${withName} are a family` : `Cannot be a family with ${withName}: ${reason}`,
      color: ok ? '#7fe06a' : '#e08a6a' }),
  })

  const addRemote = (other: { id: string; species: string; x: number; z: number;
                             name?: string; color?: string }): void => {
    const body = buildAnimal(other.species as Parameters<typeof buildAnimal>[0]).group
    body.position.set(other.x, 0, other.z)
    scene.add(body)
    remoteBodies.set(other.id, body)

    // The name sits just clear of the tallest point of whatever animal they are, so it
    // never buries itself in a bear's shoulders or floats miles over a bunny.
    const tag = createNameTag(other.name ?? 'Player', other.color ?? '#ffffff')
    const bounds = new THREE.Box3().setFromObject(body)
    tag.sprite.position.set(0, (bounds.max.y || 1) + 0.32, 0)
    body.add(tag.sprite)
    remoteTags.set(other.id, tag)
  }

  // J joins the room; V agrees to an open map vote; F asks the nearest player to be a family.
  window.addEventListener('keydown', (e) => {
    if (chat.isTyping) return
    if (e.code === 'KeyJ') room.connected ? room.disconnect() : room.connect('kate')
    if (e.code === 'KeyV') room.acceptVote()
    if (e.code === 'KeyF') {
      // Kate's family is formed with a specific other player, so pick the nearest.
      let nearest: string | null = null
      let best = 12
      for (const [id, other] of room.players) {
        const d = Math.hypot(other.x - player.position.x, other.z - player.position.z)
        if (d < best) { best = d; nearest = id }
      }
      if (nearest) room.requestFamily(nearest)
    }
  })

  // Food that grows where it grows, so the map has places worth going to.
  const berries = new BerryPatch()
  scene.add(berries.group)
  const fish = new FishShoal()
  scene.add(fish.group)

  // Competitions announce themselves and begin on their own — they were previously
  // reachable only by knowing a key, which hid the only winnable part of the game.
  const scheduler = new CompetitionScheduler()
  scheduler.onAnnounce = (kind, seconds) => {
    feedback.toast(`${COMPETITIONS[kind].name} starting in ${seconds}s — ${COMPETITIONS[kind].description}`, 'info')
    audio.play('purchase')
  }
  scheduler.onStart = (kind) => startCompetition(kind)

  // A record of every animal you have been. Progression that does not touch Kate's
  // deliberately resetting economy.
  const discovered = new Discovered()

  const bite = new Bite()
  const sniff = new SniffTrail()
  scene.add(sniff.line)


  const look = new LookControl(canvas)
  look.sensitivity = settings.current.lookSensitivity
  look.attach()
  settings.subscribe((s) => { look.sensitivity = s.lookSensitivity })

  const settingsScreen = new SettingsScreen(settings)
  window.addEventListener('keydown', (e) => {
    if (e.code === 'Escape' || e.code === 'KeyO') settingsScreen.toggle()
  })

  const hudRoot = document.querySelector<HTMLElement>('#hud')!
  const touch = new TouchControls(look, settings)
  let menuOverlayOpen = false
  const showTouch = (visible: boolean): void => {
    touch.setVisible(visible)
    // Moves the heart/hunger/thirst bars beside the D-pad.
    hudRoot.dataset.touch = visible ? 'yes' : 'no'
  }
  /**
   * Re-evaluated rather than read once at boot. Touch capability can be reported
   * differently after load (device emulation, an iPad attaching a keyboard, a browser
   * settling its media queries), and a single boot-time check left an iPad with no
   * controls at all.
   */
  const refreshTouch = (): void => {
    // Mark the document with the same answer the controls use. The stylesheet was
    // deciding separately on `(pointer: coarse)` alone, so a touch laptop --- a fine
    // pointer that still reports touch points --- got the on-screen buttons from here
    // but not the layout rules that keep the HUD off them.
    document.body.dataset.touch = TouchControls.isTouchDevice() ? 'yes' : 'no'
    // The D-pad and buttons are touch-only...
    showTouch(TouchControls.isTouchDevice() && !menuOverlayOpen)
    // ...but the MENU is for everyone. On a PC it was hidden, so the shop could only be
    // opened by knowing the P key, and pointer lock hides the cursor anyway.
    touchMenu.setVisible(!menuOverlayOpen)
  }
  window.matchMedia('(pointer: coarse)').addEventListener('change', refreshTouch)
  window.addEventListener('resize', refreshTouch)
  // Must run at boot: a returning player skips the start screen entirely, and hanging
  // the first call off its dismissal left them with no touch controls at all.
  refreshTouch()

  const keyboard = new Keyboard()
  keyboard.attach()

  // Gameplay input is suspended while Settings is open — you shouldn't walk around in
  // the menu, and it stops a rebinding keypress from also being read as movement.
  settingsScreen.onOpenChange = (open) => {
    if (open) {
      keyboard.releaseAll()
      if (document.pointerLockElement) document.exitPointerLock()
    }
    // Hide the touch controls behind the menu, or they sit on top of it.
    showTouch(!open && TouchControls.isTouchDevice())
  }

  // Temporary: cycle biomes with B so all four maps are reachable. Kate's actual rule
  // is that the group has to agree, which needs multiplayer — see M6.
  // Kate's anti-cheat line is that you cannot "make yourself into something without
  // buying it", so there is deliberately NO free-morph key — the shop is the only route.
  //
  // Changing the map is likewise hers to gate: "if everyone agrees on it". The room
  // enforces a unanimous vote, so this key only exists for offline solo play, where
  // there is no group to agree.
  const changeBiome = (next: Parameters<typeof world.setBiome>[0]): void => {
    world.setBiome(next)
    scene.remove(coupons.mesh)
    coupons.mesh.geometry.dispose()
    ;(coupons.mesh.material as THREE.Material).dispose()
    coupons = new CouponField(180, BIOMES[next].water === 'flooded' ? 0 : (BIOMES[next].water as number))
    scene.add(coupons.mesh)
    audio.play('morph')
  }

  window.addEventListener('keydown', (e) => {
    if (e.code !== 'KeyB' || room.connected) return
    changeBiome(BIOME_ORDER[(BIOME_ORDER.indexOf(world.biome) + 1) % BIOME_ORDER.length]!)
  })

  const audio = new Audio()
  // Browsers block audio until a real gesture, so unlock on the first of any.
  for (const kind of ['pointerdown', 'keydown', 'touchstart']) {
    window.addEventListener(kind, () => audio.unlock(), { once: true })
  }

  const feedback = new Feedback(hudRoot)
  const hud = new Hud(hudRoot)
  const inventoryBar = new InventoryBar(hudRoot, inventory)
  inventoryBar.onUse = (id) => {
    const item = inventory.all.find((i) => i.id === id)
    if (!item?.feeds) return
    // Kate's animals are real animals: a deer cannot eat a deer.
    if (!canEat(player.species.id, 'meat')) {
      feedback.toast(`A ${player.species.name.toLowerCase()} does not eat meat`, 'bad')
      audio.play('lose')
      return
    }
    if (player.survival.hunger >= 10) {
      feedback.toast('Not hungry yet', 'info')
      return
    }
    inventory.remove(id)
    player.survival.eat(item.feeds)
    audio.play('coupon')
    feedback.toast(`Ate ${item.label} · +${item.feeds} hunger`, 'good')
    inventoryBar.render()
  }
  const minimap = new Minimap(hudRoot)
  // Kate: seven can play, and each gets a colour. Slot 0 is the local player.
  const ownColor = PLAYER_COLORS[0]!
  minimap.setOwnColor(ownColor)

  const chat = new Chat(hudRoot)
  chat.onSend = (text) => {
    if (room.connected) room.chat(text)
    else chat.add({ from: customizeName(), text, color: ownColor })
  }
  window.addEventListener('keydown', (e) => {
    if (e.code === 'KeyT' && !chat.isTyping && !settingsScreen.isOpen && !shopScreen.isOpen) {
      e.preventDefault()
      chat.focus()
    }
  })

  const readout = document.createElement('pre')
  readout.className = 'debug-readout'
  document.querySelector('#hud')?.append(readout)

  let lastRenderTime = performance.now()
  let framesSinceReadout = 0
  let readoutTimer = 0
  let fps = 0

  const loop = new GameLoop({
    update(dt) {
      // Typing in chat must not also drive the player.
      const menuOpen = settingsScreen.isOpen || shopScreen.isOpen || cardScreen.isOpen
                     || customize.isOpen || chat.isTyping || startScreen.isOpen || mapScreen.isOpen
      const overlay = settingsScreen.isOpen || shopScreen.isOpen || cardScreen.isOpen
                    || customize.isOpen || startScreen.isOpen || mapScreen.isOpen
      if (overlay !== menuOverlayOpen) { menuOverlayOpen = overlay; refreshTouch() }
      const keys = menuOpen
        ? { forward: 0, strafe: 0, jump: false, crouch: false, sprint: false }
        : keyboard.readIntent()
      // Keyboard and touch are additive, so a touch laptop can use either.
      const ts = menuOpen ? null : touch.state
      const usingVerb = !menuOpen && (keyboard.isDown('KeyE') || touch.state.verb)

      // SOAR: hold it as a bird to climb to the canopy and take in the whole map. Kate
      // capped bird flight at tree height and that is unchanged — what soaring buys you
      // is the view, not extra altitude.
      // Holding FLY is the same intent as holding jump for a bird, so the labelled
      // button actually lifts it off the ground rather than needing a jump first.
      const flying = usingVerb && player.verb === 'fly'
      const soaring = flying && player.airborne
      soarBlend += ((soaring ? 1 : 0) - soarBlend) * Math.min(1, dt * 3)
      const intent = {
        forward: clampAxis(keys.forward + (ts?.forward ?? 0)),
        strafe: clampAxis(keys.strafe + (ts?.strafe ?? 0)),
        jump: keys.jump || (ts?.jump ?? false) || flying,
        crouch: keys.crouch || (ts?.crouch ?? false),
        sprint: keys.sprint || (ts?.sprint ?? false),
      }
      // Looking sets the heading; the keys move relative to it. That is what Kate meant
      // by "you look and you go forward to move" and by "Minecraft controls".
      player.yaw = look.yaw
      player.update(dt, intent.forward, intent.strafe, intent.jump, intent.crouch,
                    world.forest.grid, intent.sprint, usingVerb)
      // Dig is on G. Every animal can burrow, per Kate.
      const digging = !menuOpen &&
        (keyboard.isDown('KeyG') || touch.state.dig || performance.now() < digHeldUntil)
      const dug = player.updateDigging(dt, digging, burrows)
      if (digging) { digSoundTimer -= dt; if (digSoundTimer <= 0) { audio.play('dig'); digSoundTimer = 0.35 } }
      if (dug) {
        audio.play('purchase')
        feedback.toast('Burrow dug — you can sleep here', 'good')
        const mouth = new THREE.Mesh(
          new THREE.CircleGeometry(BURROW_RADIUS, 20),
          new THREE.MeshStandardMaterial({ color: 0x1a1109, roughness: 1 }),
        )
        mouth.rotation.x = -Math.PI / 2
        // Kate, 2026-08-23: "whenever you burrow, it floats." It was pinned to y=0.03
        // in world space while the ground runs from about -10 in the Hollow to +21 on
        // the Ridge, so a burrow hung in the air almost everywhere.
        mouth.position.set(dug.x, heightAt(dug.x, dug.z) + 0.03, dug.z)
        burrowMarkers.add(mouth)
      }
      player.tryShelter(burrows)

      berries.update(dt, cycle.daylight)
      fish.update(loop.simulatedTime)

      // Berries: a plant-eater standing at a ripe bush picks it, which beats grazing.
      if (usingVerb && canEat(player.species.id, 'plants')
          && berries.ripeNear(player.position.x, player.position.z) && forageCooldown <= 0) {
        if (berries.pick(player.position.x, player.position.z)) {
          forageCooldown = 1
          player.survival.eat(BERRY_FEEDS)
          audio.play('coupon')
          feedback.toast(`Ate berries · +${BERRY_FEEDS} hunger`, 'good')
        }
      }
      // Fish: a meat-eater standing in the water can catch one.
      if (usingVerb && canEat(player.species.id, 'meat') && player.inWater && forageCooldown <= 0) {
        if (fish.catchOne(player.position.x, player.position.z)) {
          forageCooldown = 1.2
          inventory.add({ id: 'fish', label: 'Fish', icon: '🐟', feeds: FISH_FEEDS })
          inventoryBar.render()
          audio.play('coupon')
          feedback.toast('Caught a fish', 'good')
        }
      }
      forageCooldown = Math.max(0, forageCooldown - dt)

      // GRAZE: plant-eaters feed off the world instead of carrying meat around.
      player.grazing = usingVerb && (player.verb === 'graze' || player.inWater)
      if (usingVerb && player.verb === 'graze' && !player.inWater) {
        grazeProgress += dt
        if (grazeProgress >= 2) {
          grazeProgress = 0
          if (player.survival.hunger >= 10) {
            feedback.toast('Not hungry', 'info')
          } else {
            player.survival.eat(1)
            audio.play('step')
            feedback.toast('Grazed · +1 hunger', 'good')
          }
        }
      } else {
        grazeProgress = 0
      }

      // Sleep: night, in a burrow, and never forced — her original rule, now with a button.
      const night = cycle.phase === 'night'
      touch.setNight(night)
      const wantsSleep = !menuOpen && (touch.state.sleep || keyboard.isDown('KeyZ'))
      // Her rule, species by species: a bird needs a nest, a human needs nothing at
      // all, everything else needs a burrow.
      const needsToSleep = player.species.id === 'human' ? 'nothing' as const
        : player.species.id === 'bird' ? 'nest' as const : 'burrow' as const
      const shelter = player.shelteredIn
        ? (player.species.id === 'bird' ? 'nest' as const : 'burrow' as const)
        : null
      if (wantsSleep && !player.survival.asleep) {
        if (player.survival.trySleep(night, shelter, needsToSleep)) {
          audio.play('purchase')
          feedback.toast('Sleeping until morning', 'good')
        } else if (sleepHintTimer <= 0) {
          const missing = needsToSleep === 'nest' ? 'You need a nest to sleep in — build one'
            : 'You need a burrow to sleep in — dig one'
          feedback.toast(night ? missing : 'You can only sleep at night', 'info')
          sleepHintTimer = 4
        }
      }
      if (player.survival.asleep) {
        // A night's sleep mends you --- her answer to "I need to be able to heal".
        player.survival.heal(dt * 60 * 0.02)
        // Sleeping runs the clock forward fast until it is day again.
        cycle.advance(dt * 60)
        if (cycle.phase === 'day') {
          player.survival.wake()
          feedback.toast('Morning', 'good')
        }
      }
      sleepHintTimer = Math.max(0, sleepHintTimer - dt)

      // "To collect coupons, you run through them."
      const picked = coupons.update(dt, player.position.x, player.position.z)
      if (picked > 0) {
        economy.collect(picked)
        audio.play('coupon')
        feedback.toast(`+${picked} coupons`, 'good')
      }
      economy.update(dt)
      shopScreen.setContext({ speciesId: player.species.id, biome: world.biome })

      // Death charges 50 coupons and respawns you at the middle of the map.
      if (player.survival.dead) {
        // "When you die, you should lose all your stuff." That now means everything,
        // not just the 50 coupons she priced respawning at on 08-16.
        const lostCoupons = economy.chargeRespawn()
        const lostItems = inventory.all.length
        const lostWeapons = economy.weapons.size
        inventory.clear()
        economy.weapons.clear()
        economy.pantry.clear()
        economy.powerUpRemaining = 0
        inventoryBar.render()
        player.morph('human')
        player.survival.respawn()
        player.position.set(0, 0.9, 0)
        player.previous.copy(player.position)
        audio.play('lose')
        feedback.toast(`You died — lost ${lostCoupons} coupons, ${lostItems} items, ${lostWeapons} weapons`, 'bad')
      }

      // Kate reversed her own rule after playing: offline mobs hunt you. Online they
      // wander, because there they stand in for other players.
      let mobDamage = 0
      const prey = room.connected ? null : { x: player.position.x, z: player.position.z }
      // Online there is no prey to measure against, so pass the player position purely
      // for the fur LOD distance.
      const lodFrom = { x: player.position.x, z: player.position.z }
      for (const animal of animals) {
        // What you ARE decides what they do — a deer flees a wolf and ignores a bunny.
        animal.preySpecies = player.species.id
        // Kate's night has to be worth preparing for. Predators hunt harder in the dark,
        // which is what gives the daytime a purpose.
        animal.nightBoost = 1 + (1 - cycle.daylight) * 1.1
        // Freezing and camouflage both make you harder to notice.
        // A burrow is the shelter Kate asked for: inside one, nothing can find you.
        animal.preyHidden = Math.max(player.frozen, player.camouflage.strength,
                                     player.shelteredIn ? 1 : 0)
        mobDamage += animal.update(dt, prey ?? null, animals, lodFrom)
      }
      if (mobDamage > 0) player.survival.damage(mobDamage)

      // TRAMPLE — everything nearby scatters, and the sound carries.
      if (player.trampledThisStep && trampleCooldown <= 0) {
        trampleCooldown = 1.1
        audio.play('hit')
        let scattered = 0
        for (const animal of animals) {
          if (animal.isDead()) continue
          if (Math.hypot(animal.x - player.position.x, animal.z - player.position.z) < TRAMPLE_RADIUS * 2.5) {
            animal.scatter()
            scattered++
          }
        }
        if (scattered) feedback.toast(`${scattered} animals scattered`, 'good')
      }
      trampleCooldown = Math.max(0, trampleCooldown - dt)

      // Bite: hold attack on a target for 5 continuous seconds to kill it.
      // Kate's prey animals have speed for a weapon, not teeth. A bunny cannot fight.
      const attacking = !menuOpen && player.canFight &&
        (keyboard.isDown(settings.current.attackKey) || touch.state.attack)
      if (!menuOpen && !player.canFight && (keyboard.isDown(settings.current.attackKey) || touch.state.attack)
          && cannotFightTimer <= 0) {
        feedback.toast(`A ${player.species.name.toLowerCase()} cannot fight — run, or hold still`, 'bad')
        cannotFightTimer = 4
      }
      cannotFightTimer = Math.max(0, cannotFightTimer - dt)
      bite.setAttackerPosition(player.position.x, player.position.z)
      // Stay latched onto whatever is already being bitten. Recomputing "nearest" every
      // frame meant that once mobs turned hostile and swarmed, the target flipped
      // constantly and the five-second hold could never complete — kills became
      // impossible the moment Kate's hostile-mob rule landed.
      let nearest: AnimalAgent | null = null
      if (bite.targetId) {
        const held = animals.find((a) => a.id === bite.targetId && !a.isDead())
        if (held && Math.hypot(held.x - player.position.x, held.z - player.position.z) <= BITE_RANGE) {
          nearest = held
        }
      }
      if (!nearest) {
        let nearestDistance = BITE_RANGE
        for (const animal of animals) {
          if (animal.isDead()) continue
          const distance = Math.hypot(animal.x - player.position.x, animal.z - player.position.z)
          if (distance <= nearestDistance) { nearest = animal; nearestDistance = distance }
        }
      }
      // Kate's one predation rule: a cat kills a bird outright.
      const wasBiting = bite.targetId !== null
      if (attacking && nearest && killsInstantly(player.species, nearest.speciesId)) {
        nearest.kill()
        competition?.recordKill()
        audio.play('kill')
        dropMeat(nearest.speciesId)
      } else if (bite.update(dt, attacking, player.species, nearest)) {
        competition?.recordKill()
        audio.play('kill')
        if (nearest) dropMeat(nearest.speciesId)
      }

      // Show the five-second hold, and say why a press did nothing when nothing is near.
      player.biting = bite.targetId !== null
      if (bite.targetId && nearest) {
        if (!wasBiting) audio.play('bite')
        biteSoundTimer -= dt
        if (biteSoundTimer <= 0) { audio.play('hit'); biteSoundTimer = 0.42 }
        feedback.showBite(bite.progress, nearest.hearts / 24,
                          SPECIES[nearest.speciesId].name)
      } else {
        feedback.hideBite()
        if (attacking && !nearest && attackHintTimer <= 0) {
          feedback.toast('Nothing close enough to bite — get right next to an animal', 'info')
          attackHintTimer = 4
        }
      }
      attackHintTimer = Math.max(0, attackHintTimer - dt)

      sniff.update(player.species, player.crouching, player.position.x, player.position.z, animals)

      scheduler.update(dt)
      if (competition && !competition.finished) {
        competition.update(dt, player.position.x, player.position.z,
                           player.species, player.camouflage.strength, animals)
        // Dim a checkpoint once it's banked, so the next one reads as the target.
        competition.checkpoints.forEach((point, i) => {
          const marker = checkpointMarkers.children[i] as THREE.Mesh | undefined
          if (marker) {
            const material = marker.material as THREE.MeshStandardMaterial
            material.emissiveIntensity = point.reached ? 0.05 : 0.7
            material.color.setHex(point.reached ? 0x2c4a52 : 0x6de3ff)
          }
        })
        if (competition.finished && competition.result) {
          scheduler.finished()
          const { won, detail } = competition.result
          // Winning is the ONLY source of a card, and which one is random.
          audio.play(won ? 'win' : 'lose')
          feedback.toast(won ? `You won! ${detail}` : `You lost — ${detail}`, won ? 'good' : 'bad')
          if (won) {
            audio.play('card')
            const { kind, upgraded } = cards.award()
            chat.add({ from: 'Card',
                       text: upgraded
                         ? `${kind.realName} upgraded to level ${cards.get(kind.species)!.level}!`
                         : `You won a ${kind.realName} card (${kind.rarity})`,
                       color: '#f0d060' })
            // Somebody wants to buy it — her offer-and-accept trade.
            const rival = cards.makeIncomingOffer('Wolf_7', kind.species)
            if (rival) chat.add({ from: 'Wolf_7',
              text: `i'll give you ${rival.askingPrice} coupons for that ${kindOf(kind.species)!.realName} — press C`,
              color: '#4fc76b' })
          }
          chat.add({ from: 'Game',
                     text: `${won ? 'You won' : 'You lost'} the ${COMPETITIONS[competition.kind].name.toLowerCase()} — ${detail}`,
                     color: won ? '#7fe06a' : '#e08a6a' })
        }
      }

      // Jumping, landing and footsteps.
      const groundedNow = player.position.y <= 0.901
      if (wasGrounded && !groundedNow) audio.play('jump')
      if (!wasGrounded && groundedNow) audio.play('land')
      wasGrounded = groundedNow
      if (groundedNow && (intent.forward !== 0 || intent.strafe !== 0)) {
        stepTimer -= dt * (player.sprinting ? 1.7 : 1)
        if (stepTimer <= 0) { audio.play('step'); stepTimer = 0.42 }
      }

      // One warning as the light goes, so night arriving is a thing that happens to you
      // rather than something you notice once it is too late.
      const wasNight = nightNow
      nightNow = cycle.phase === 'night'
      if (nightNow && !wasNight) {
        feedback.toast(burrows.all.length > 0
          ? 'Night is falling — get to your burrow'
          : 'Night is falling — dig a burrow (G) before they find you', 'bad')
        audio.play('hurt')
      } else if (!nightNow && wasNight) {
        feedback.toast('Morning', 'good')
      }

      cycle.advance(dt)
      weather.update(dt, player.position)

      // Rain is a continuous bed; thunder cracks with the lightning flash.
      if (weather.current !== lastWeather) {
        audio.setRain(weather.isWet, weather.current === 'thunderstorm')
        lastWeather = weather.current
      }
      if (weather.flash > 0.9) audio.play('thunder')

      // The jungle bed and the music follow the same clock and weather as everything else.
      audio.updateAmbience(dt, cycle.daylight, weather.isWet)

      // Day/night sets the base lighting; weather then dims it and closes the fog in.
      // Both are floored afterwards: a storm at midnight otherwise multiplies the
      // moonlight down to near-black, and Kate's night is meant to be playable —
      // sleeping is optional, so you have to be able to see while awake at night.
      cycle.apply(scene, world.sun, world.sky, world.moon)
      // Drive the sky dome from the same clock, so the sun is where the light says it is.
      const horizonCloseness = 1 - Math.min(1, Math.max(0, Math.sin(Math.max(cycle.sunElevation, 0)) * 3))
      world.sky3d.update(cycle.daylight, world.sun.position, horizonCloseness)
      // Water reflects whatever the sky is doing, which is how you see the moon on it.
      // The water gets the cubemap on its own material rather than relying on the
      // scene's: envMapIntensity only scales a material's own envMap, and the water needs
      // to be turned up at night without brightening everything else with it.
      if (skyEnv.update(scene, cycle.daylight)) {
        world.waterMaterial.envMap = skyEnv.texture
        world.waterMaterial.needsUpdate = true
      }
      // The reflected night sky is nearly black, so amplifying it picks out the moon and
      // the stars on the surface without lifting the dark around them. In daylight the
      // same amplification reflected the *bright* sky just as faithfully and turned the
      // pond into a flat white disc --- Kate's "big white circle in the middle". So it
      // is turned up only as the light goes.
      world.waterMaterial.envMapIntensity = 1.8 - cycle.daylight * 1.05
      // Dark water is what makes a moon reflection legible: a bright surface swamps the
      // glint, so the pond loses its own colour at night and keeps only what it reflects.
      world.waterMaterial.color.copy(NIGHT_WATER).lerp(DAY_WATER, cycle.daylight)
      world.sky3d.mesh.position.copy(player.position)
      world.sun.intensity = Math.max(MIN_SUN_INTENSITY, world.sun.intensity * weather.lightScale)
      world.moon.intensity *= weather.lightScale
      world.sky.intensity = Math.max(MIN_SKY_INTENSITY, world.sky.intensity * weather.lightScale)
      world.fog.near = world.baseFog.near * weather.fogScale
      world.fog.far = world.baseFog.far * weather.fogScale
      if (weather.flash > 0) {
        world.sun.intensity += weather.flash * 3.5
        world.sky.intensity += weather.flash * 1.5
      }
    },
    render(alpha) {
      const now = performance.now()
      const frameSeconds = Math.min((now - lastRenderTime) / 1000, 0.1)
      lastRenderTime = now

      player.applyInterpolated(alpha)
      // No sun, no contact shadow: it fades to a hint by moonlight.
      player.shadowStrength = 0.18 + cycle.daylight * 0.82
      hud.update(player.survival, player.shelteredIn !== null)

      if (player.survival.hearts < lastHearts - 0.01) { audio.play('hurt'); player.flinch = 1 }
      lastHearts = player.survival.hearts

      room.move(player.position.x, player.position.z, player.yaw, now)
      for (const [id, other] of room.players) {
        const body = remoteBodies.get(id)
        if (body) {
          body.position.set(other.x, 0, other.z)
          body.rotation.y = other.yaw
        }
        const tag = remoteTags.get(id)
        if (!tag) continue
        const label = `${other.name}\u0000${other.color}`
        if (tag.sprite.userData.label !== label) {
          tag.set(other.name, other.color)
          tag.sprite.userData.label = label
        }
        tag.faceCamera(Math.hypot(other.x - rig.camera.position.x, other.z - rig.camera.position.z))
      }

      // Other players' dots. The AI animals stand in as the other players for now —
      // Kate called them "AI people playing the game". The local player's own dot is
      // deliberately absent: "other people, not yourself".
      const dots: MapDot[] = room.connected
        ? [...room.players.values()].map((o) => ({ id: o.id, x: o.x, z: o.z, color: o.color }))
        : animals
            .filter((a) => !a.isDead())
            .slice(0, PLAYER_COLORS.length - 1)
            .map((a, i) => ({ id: a.id, x: a.x, z: a.z, color: PLAYER_COLORS[i + 1]! }))
      minimap.update(player.position.x, player.position.z, player.yaw, dots)
      rig.setSoar(soarBlend)
      rig.follow(player.object.position, player.yaw, frameSeconds, look.pitch,
                 player.speedOverGround, player.leanAmount,
                 // A human is the yardstick: the camera frames every other animal
                 // against how big it is compared to one.
                 player.eyeHeight / HUMAN_EYE_HEIGHT)
      // Earthquakes shake the view. Zero for every other weather.
      rig.camera.position.add(weather.shake)
      renderer.render(scene, rig.camera)

      framesSinceReadout++
      readoutTimer += frameSeconds
      if (readoutTimer >= 0.5) {
        fps = Math.round(framesSinceReadout / readoutTimer)
        framesSinceReadout = 0
        readoutTimer = 0
      }
      readout.textContent =
        `MENU (top-left) → Shop to buy a morph · Esc frees the mouse\n` +
        `ANIMAL ATTACK — M1\n` +
        `${fps} fps · sim ${loop.simulatedTime.toFixed(1)}s\n` +
        `canvas ${canvas.clientWidth}x${canvas.clientHeight} @${window.devicePixelRatio.toFixed(1)}x\n` +
        `pos ${player.position.x.toFixed(1)}, ${player.position.z.toFixed(1)}\n` +
        `bumps ${player.treeBumps}${player.inWater ? ' · in water' : ''}` +
        `${player.sprinting ? ' · SPRINTING' : ''}\n` +
        `${cycle.clockLabel} · ${weather.current}\n` +
        `biome ${BIOMES[world.biome].name}${room.connected ? '' : ' (B)'} · ${player.species.name}\n` +
        `str ${player.species.strength} · spd ${player.species.speed.toFixed(1)} · ` +
        `${player.species.abilities.join(' ')}\n` +
        `${bite.targetId ? `biting ${bite.targetId} ${(bite.progress*100).toFixed(0)}%` : ''}` +
        `${sniff.visible ? ` · sniffing${sniff.target ? ` → ${sniff.target.id}` : ' (nothing near)'}` : ''}` +
        `${player.flying ? ' · FLYING' : ''}${player.climbing ? ' · CLIMBING' : ''}` +
        `${player.camouflage.strength > 0.05 ? ` · hidden ${(player.camouflage.strength*100).toFixed(0)}%` : ''}\n` +
        `${competition && !competition.finished
            ? `${COMPETITIONS[competition.kind].name} · ${competition.timeLeft.toFixed(0)}s left` +
              (competition.kind === 'obstacle-course'
                ? ` · ${competition.checkpointsReached}/${competition.checkpoints.length}`
                : competition.kind === 'kill-match'
                  ? ` · ${competition.kills}-${competition.rivalBestKills}`
                  : competition.foundAt === null ? ' · hidden' : ' · found')
            : 'K to start a competition'}\n` +
        `${room.connected ? `online · ${room.players.size + 1} players · F family · V vote` : 'offline · J to join'}\n` +
        `${scheduler.label} · discovered ${discovered.count}/${discovered.total}\n` +
        `${player.verb ? `${VERBS[player.verb].label} (E)` : 'no special move'}` +
        `${player.frozen > 0.05 ? ` · HIDDEN ${(player.frozen*100).toFixed(0)}%` : ''}` +
        `${player.canFight ? '' : ' · cannot fight'}\n` +
        `berries ${berries.ripeCount} ripe · fish ${fish.remaining}\n` +
        `${economy.coupons} coupons · ${coupons.remaining} on map · P shop · C cards (${cards.distinctCount}/12)` +
        `${economy.powerUpActive ? ` · POWERED ${Math.ceil(economy.powerUpRemaining/60)}m` : ''}` +
        `${economy.weapons.size ? ` · ${[...economy.weapons].join(', ')}` : ''}\n` +
        `burrows ${burrows.all.length}${player.shelteredIn ? ' · SHELTERED' : ''}` +
        `${player.digProgress > 0 ? ` · digging ${player.digProgress.toFixed(1)}s` : ''}\n` +
        `${look.locked ? 'mouse looks' : 'CLICK to look'} · arrows/WASD move · space jump · shift crouch\n` +
        `attack ${keyLabel(settings.current.attackKey)} · ctrl sprint · O settings` +
        `${player.crouching ? ' · CROUCHING' : ''}`
    },
  })

  loop.start()

  if (import.meta.env.DEV) {
    // Dev-only handle so the loop can be stepped and inspected without rAF, which a
    // hidden or headless tab never fires. Stripped from production builds.
    ;(window as unknown as { __aa: unknown }).__aa = { loop, player, rig, keyboard, scene, renderer, world, cycle, weather, look, settings, settingsScreen, touch, burrows, skyEnv, SPECIES, SPECIES_ORDER, animals, bite, sniff, economy, shopScreen, chat, minimap, startCompetition, cards, cardScreen, CARD_KINDS, customize, room, touchMenu, startScreen, audio, feedback, inventory, inventoryBar, mapScreen, heightAt, LANDMARKS, berries, fish, scheduler, discovered, buildAnimal, animate, THREE, getCompetition: () => competition,
      /**
       * Opening a screen for a test, with the answer to "is it actually on screen?"
       * built in. Four separate checks this session silently measured hidden or absent
       * elements and reported a pass --- a toggle left the menu sheet closed, or a
       * panel never rendered --- so this refuses to pretend.
       */
      /**
       * Measure every animal against the real one. Kate's "they do not look like real
       * life" is partly a matter of taste and partly not: proportion is checkable, and
       * a wolf standing at half its real height reads as a toy whatever else is right.
       */
      proportions: (): Record<string, unknown> => {
        const rows: Record<string, unknown> = {}
        let pass = 0
        let total = 0
        for (const id of Object.keys(REAL_ANIMALS)) {
          const model = buildAnimal(id as Parameters<typeof buildAnimal>[0])
          const size = new THREE.Vector3()
          new THREE.Box3().setFromObject(model.group).getSize(size)
          let torso: THREE.Mesh | null = null
          model.group.traverse((n) => {
            if (!torso && n instanceof THREE.Mesh
                && (n.geometry.attributes.position?.count ?? 0) > 400) torso = n
          })
          if (!torso) { rows[id] = 'no torso mesh found'; continue }
          const bodyLength = Math.max(size.x, size.z)
          const clearance = new THREE.Box3().setFromObject(torso).min.y / bodyLength
          const want = realClearanceRatio(id)!
          const error = (clearance - want) / want
          total += 1
          if (Math.abs(error) <= PROPORTION_TOLERANCE) pass += 1
          rows[id] = { real: +want.toFixed(2), ours: +clearance.toFixed(2),
                       errorPct: Math.round(error * 100),
                       ok: Math.abs(error) <= PROPORTION_TOLERANCE }
        }
        return { rows, passing: `${pass} of ${total}`,
                 tolerance: `${PROPORTION_TOLERANCE * 100}%` }
      },
      ui: {
        /** Rendered and non-trivial in size, which getComputedStyle alone will not tell you. */
        shown: (selector: string): boolean => {
          const el = document.querySelector(selector)
          if (!el) return false
          const rect = el.getBoundingClientRect()
          return rect.width > 20 && rect.height > 10
        },
        /** Force one screen open from a known state. Throws rather than measure nothing. */
        open: (name: 'shop' | 'cards' | 'map' | 'settings' | 'name' | 'help'): void => {
          closeScreensExcept(name)
          if (name === 'shop') shopScreen.open({ speciesId: player.species.id, biome: world.biome })
          else if (name === 'cards') cardScreen.open()
          else if (name === 'map') mapScreen.open()
          else if (name === 'settings') settingsScreen.open()
          else if (name === 'name') customize.open()
          else startScreen.open()
          const selector = { shop: '.shop', cards: '.cards', map: '.mapscreen',
                             settings: '.settings', name: '.customize', help: '.start' }[name]
          const el = document.querySelector(selector)
          const rect = el?.getBoundingClientRect()
          if (!rect || rect.width < 20) throw new Error(`${name} did not render — nothing to measure`)
        },
        closeAll: (): void => { closeScreensExcept('none') },
        /**
         * The window's size, or a throw. When the browser pane is hidden the window
         * reports 0x0, and any check comparing element rects against it decides every
         * element is off screen --- which is how a whole UI sweep came back reporting
         * six broken screens that were all fine.
         */
        viewport: (): { w: number; h: number } => {
          const w = window.innerWidth
          const h = window.innerHeight
          if (w < 50 || h < 50) {
            throw new Error(`window reports ${w}x${h} — the pane is hidden, so nothing measured against it means anything`)
          }
          return { w, h }
        },
      },
      getCoupons: () => coupons }
  }
}

boot()
